import { Grid, IconButton, useEventCallback } from '@mui/material';
import EnrollmentList from 'shell/components/shared/enrollment-list';
import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import ParenthoodRelationshipList from 'shell/components/shared/parenthood-relationship-list';
import { HeadCell } from 'shell/components/shared/table/types';
import { noop } from 'shell/utils/noop';
import AireList from 'system/components/shared/aire-list';
import Table from 'shell/components/shared/table';
import { TableFamilyVM } from './table-person';
import { FamilyVM } from '../family/table-family';
import Delete from '@mui/icons-material/Delete';
import CfControlList from '../family/cf-control-list';
import filterParenthood from 'shell/components/shared/parenthood-relationship-list/filter-parenthood';

export const headCells: HeadCell<TableFamilyVM, keyof TableFamilyVM>[] = [
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'fiscalCode',
    label: 'CF',
  },
  {
    id: 'birthDate',
    label: 'Data di nascita',
  },
  {
    id: 'birthplace',
    label: 'Luogo di nascita',
  },
  {
    id: 'residence',
    label: 'Residenza',
  },
  {
    id: 'relationshipElement',
    label: 'Parentela',
  },
  {
    id: 'drivingLicenseElement',
    label: 'Patente',
  },
  {
    id: 'autoElement',
    label: 'Auto',
  },
  {
    id: 'aireElement',
    label: 'AIRE',
  },
  {
    id: 'cfControlElement',
    label: 'Controllo dati AE',
  },
  {
    id: 'action',
    label: 'Azioni',
  },
];

interface TableNewFamilyProps {
  disabled?: boolean;
  values: Array<FamilyVM>;
  onChange: (familyMembers: Array<FamilyVM>) => void;
  onDeletePress: (value: FamilyVM, index: number) => void;
}

const TableNewFamilyComponent = ({
  values,
  onChange,
  disabled,
  onDeletePress,
}: TableNewFamilyProps): React.ReactElement => {
  const [tableProps, setTableProps] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  const onChangePage = useEventCallback((page: number) => {
    setTableProps({
      rowsPerPage: tableProps.rowsPerPage,
      page,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setTableProps({
      rowsPerPage,
      page: 0,
    });
  });
  const onChangeRelationship = useCallback(
    (value, itemSelected) => {
      const newItem = {
        ...itemSelected,
        relationship: value,
      };

      let practicesNew = values?.slice() || [];

      const isSelectedPractice = !!values?.find(
        (item) => item.fiscalCode === itemSelected.fiscalCode
      );

      if (isSelectedPractice) {
        practicesNew = practicesNew.map((operation) =>
          operation.fiscalCode !== itemSelected.fiscalCode ? operation : newItem
        );

        onChange(practicesNew);
        return;
      }

      practicesNew.push(newItem);
      onChange(practicesNew);
    },
    [onChange, values]
  );

  const onChangeDriving = useCallback(
    (value, itemSelected) => {
      const newItem = {
        ...itemSelected,
        drivingLicense: value,
      };

      let practicesNew = values?.slice() || [];

      const isSelectedPractice = !!values?.find(
        (item) => item.fiscalCode === itemSelected.fiscalCode
      );

      if (isSelectedPractice) {
        practicesNew = practicesNew.map((operation) =>
          operation.fiscalCode !== itemSelected.fiscalCode ? operation : newItem
        );

        onChange(practicesNew);
        return;
      }

      practicesNew.push(newItem);
      onChange(practicesNew);
    },
    [onChange, values]
  );

  const onChangeAuto = useCallback(
    (value, itemSelected) => {
      const newItem = {
        ...itemSelected,
        auto: value,
      };

      let practicesNew = values?.slice() || [];

      const isSelectedPractice = !!values?.find(
        (item) => item.fiscalCode === itemSelected.fiscalCode
      );

      if (isSelectedPractice) {
        practicesNew = practicesNew.map((operation) =>
          operation.fiscalCode !== itemSelected.fiscalCode ? operation : newItem
        );

        onChange(practicesNew);
        return;
      }

      practicesNew.push(newItem);
      onChange(practicesNew);
    },
    [onChange, values]
  );

  const onChangeCfControl = useEventCallback((itemSelected: FamilyVM, value?: string) => {
    const newItem: FamilyVM = {
      ...itemSelected,
      cfControl: value,
    };

    let practicesNew: Array<FamilyVM> = values?.slice() || [];

    const isSelectedPractice = !!values?.find(
      (item) =>
        item.fiscalCode === itemSelected.fiscalCode &&
        item.idSchedaSoggettoANPR === itemSelected.idSchedaSoggettoANPR &&
        item.idIntestatario === itemSelected.idIntestatario
    );

    if (isSelectedPractice) {
      practicesNew = practicesNew.map((operation) =>
        !(
          operation.fiscalCode === itemSelected.fiscalCode &&
          operation.idSchedaSoggettoANPR === itemSelected.idSchedaSoggettoANPR &&
          operation.idIntestatario === itemSelected.idIntestatario
        )
          ? operation
          : newItem
      ) as Array<FamilyVM>;

      onChange(practicesNew);
      return;
    }

    practicesNew.push(newItem);
    onChange(practicesNew);
  });

  const body: Array<TableFamilyVM> = useMemo(() => {
    return values?.slice().map((item, index) => {
      const element = { ...item };

      return {
        ...item,
        relationshipElement: (
          <ParenthoodRelationshipList
            id={`${item.fiscalCode}`}
            value={item.relationship || ''}
            onChange={(value) => onChangeRelationship(value, element)}
            disabled={disabled}
            filterParenthood={filterParenthood}
            label=""
          />
        ),
        drivingLicenseElement: (
          <EnrollmentList
            id={item.fiscalCode}
            value={item.drivingLicense || ''}
            onChange={(value) => onChangeDriving(value, item)}
            disabled={disabled}
            label=""
          />
        ),
        autoElement: (
          <EnrollmentList
            id={item.fiscalCode}
            value={item.auto || ''}
            onChange={(value) => onChangeAuto(value, item)}
            disabled={disabled}
            label=""
          />
        ),
        aireElement: <AireList disabled id={item.fiscalCode} value={item.aire} onChange={noop} />,
        cfControlElement: (
          <CfControlList
            label=""
            id={item.fiscalCode}
            value={item.cfControl || ''}
            onChange={(value) => onChangeCfControl(item, value)}
          />
        ),
        action: (
          <Fragment>
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onDeletePress(item, index);
              }}
              aria-label="Elimina elemento"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
              color="cdmBrand"
            >
              <Delete />
            </IconButton>
          </Fragment>
        ),
      };
    }) as Array<TableFamilyVM>;
  }, [values, onChangeRelationship, onChangeDriving, onChangeAuto, onChangeCfControl]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Table
          ariaLabel="Lista Persone"
          page={tableProps.page}
          rowsPerPage={tableProps.rowsPerPage}
          body={body}
          count={body?.length || 0}
          head={headCells}
          onSort={noop}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          orders={{}}
          isNotLayoutFixed
          noSort
          hideButtons
        />
      </Grid>
    </Grid>
  );
};

TableNewFamilyComponent.displayName = 'Table New Family form component';

const TableNewFamily = memo(TableNewFamilyComponent);

export default TableNewFamily;
